import { useConsentManager } from '@c15t/nextjs/headless';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import type { ConsentCategoryName, CustomizeEntry } from './types';

type ConsentUiContextValue = {
  customizeEntry: CustomizeEntry;
  openPreferences: () => void;
  showCustomizeFromBanner: () => void;
  showNotice: () => void;
  dismiss: () => void;
};

const ConsentUiContext = createContext<ConsentUiContextValue | null>(null);

const useConsentUiContext = () => {
  const value = useContext(ConsentUiContext);
  if (!value) {
    throw new Error('Consent UI hooks must be used within ConsentManager');
  }
  return value;
};

type Props = {
  children: ReactNode;
};

/**
 * UI navigation around c15t's activeUI. Tracks how customize was opened so we
 * show Back (from banner) vs Close (from Account settings).
 */
export const ConsentUiProvider = ({ children }: Props) => {
  const { consents, setSelectedConsent, setActiveUI } = useConsentManager();
  const [customizeEntry, setCustomizeEntry] =
    useState<CustomizeEntry>('banner');

  // Discard unsaved toggle edits when leaving customize without saving.
  const resetSelectedToSaved = useCallback(() => {
    (Object.entries(consents) as Array<[ConsentCategoryName, boolean]>).forEach(
      ([name, value]) => {
        setSelectedConsent(name, value);
      },
    );
  }, [consents, setSelectedConsent]);

  const openPreferences = useCallback(() => {
    setCustomizeEntry('direct');
    setActiveUI('dialog', { force: true });
  }, [setActiveUI]);

  const showCustomizeFromBanner = useCallback(() => {
    setCustomizeEntry('banner');
    setActiveUI('dialog');
  }, [setActiveUI]);

  const showNotice = useCallback(() => {
    resetSelectedToSaved();
    setActiveUI('banner');
  }, [resetSelectedToSaved, setActiveUI]);

  const dismiss = useCallback(() => {
    resetSelectedToSaved();
    setActiveUI('none');
  }, [resetSelectedToSaved, setActiveUI]);

  const value = useMemo(
    () => ({
      customizeEntry,
      openPreferences,
      showCustomizeFromBanner,
      showNotice,
      dismiss,
    }),
    [
      customizeEntry,
      openPreferences,
      showCustomizeFromBanner,
      showNotice,
      dismiss,
    ],
  );

  return (
    <ConsentUiContext.Provider value={value}>
      {children}
    </ConsentUiContext.Provider>
  );
};

export { useConsentUiContext };
